import {map, Observable} from 'rxjs';
import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import {FormBuilder, FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbActiveModal, NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {TranslatePipe} from '@brunata-angular/util';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {AccountPeriod} from '../reporting-costs.component';
import {AccountPeriodValidator} from './account-period-validator';
import {ControlMessagesComponent} from '@brunata-angular/ui';
import {AccountPeriodControl} from '../reporting-form-controls';

type PeriodWarnings = {
    periodWarning6Months: boolean;
    periodWarning18Months: boolean;
    startDateIsNotTheDayAfterThePreviousAccountingPeriod: boolean;
};

@Component({
    selector: 'app-edit-account-period',
    templateUrl: './edit-account-period-modal.component.html',
    styleUrls: ['./edit-account-period-modal.component.scss'],
    providers: [ToastrService],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslatePipe,
        ReactiveFormsModule,
        NgbModule,
        InputDateGroupComponent,
        ControlMessagesComponent,
    ],
})
export class EditAccountPeriodModalComponent implements OnInit {
    @Input() startDate: DateTime | undefined;
    @Input() endDate: DateTime | undefined;
    @Output() submitData = new EventEmitter<AccountPeriod>();

    formGroup: AccountPeriodControl = this.formBuilder.group(
        {
            startDate: new FormControl<DateTime | null>(null, Validators.required),
            endDate: new FormControl<DateTime | null>(null, Validators.required),
        },
        {validators: AccountPeriodValidator.endDateIsAfterStartDateValidator},
    );

    public readonly accountPeriodWarnings$: Observable<PeriodWarnings> =
        this.formGroup.valueChanges.pipe(
            map(it => ({
                periodWarning6Months:
                    it.startDate != null &&
                    it.endDate != null &&
                    it.startDate.startOf('day') >= it.endDate.startOf('day').minus({months: 6}),

                periodWarning18Months:
                    it.startDate != null &&
                    it.endDate != null &&
                    it.startDate.startOf('day') <= it.endDate.startOf('day').minus({months: 18}),
                startDateIsNotTheDayAfterThePreviousAccountingPeriod:
                    it.startDate != null &&
                    this.startDate != null &&
                    it.startDate.toMillis() !== this.startDate.toMillis(),
            })),
        );

    constructor(
        public activeModal: NgbActiveModal,
        private formBuilder: FormBuilder,
    ) {}

    submit(): void {
        this.formGroup.markAllAsTouched();
        if (this.formGroup.valid) {
            this.submitData.emit(this.formGroup.getRawValue());
            this.activeModal.close();
        }
    }

    ngOnInit(): void {
        if (this.startDate !== undefined) {
            this.formGroup.controls.startDate.setValue(this.startDate);
        }
        if (this.endDate !== undefined) {
            this.formGroup.controls.endDate.setValue(this.endDate);
        }
    }
}
